<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { z } from 'zod';
import {
    UiForm,
    UiFormField,
    UiInputField,
    UiButton,
    UiText,
    AmbientFabricBackground,
    UiLogo,
    useToaster
} from '@shopexperts/shared';
import { useAuthStore } from '@/store/auth';
import { useExpertStore } from '@/store/expert';
import { redirectToDashboard } from '@/router/index';
import AnnouncementBar from '@/components/common/AnnouncementBar.vue';
import { authHandoffQuery, isMessageRedirect } from '@/v2Projects/directMessageDraft';
import { getSafeClientRedirect } from '@/app/utils/directReview';
import { getSafeExpertCheckoutRedirect } from '@/app/composables/expert-billing/expertCheckout';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const expertStore = useExpertStore();
const toaster = useToaster();

const isLoading = ref(false);
const messageHandoff = computed(() => isMessageRedirect(route.query.redirect));
const handoffQuery = computed(() => authHandoffQuery(loginModel.value.email, route.query.redirect));
const accountError = ref('');

const loginModel = ref({
    email: typeof route.query.email === 'string' ? route.query.email.trim() : '',
    password: '',
});

const initialValues = { ...loginModel.value };

const loginSchema = z.object({
    email: z.preprocess(
        (val) => val === undefined || val === null ? '' : val,
        z.string()
            .min(1, 'Email is required')
            .email('Please enter a valid email address')
    ),
    password: z.preprocess(
        (val) => val === undefined || val === null ? '' : val,
        z.string().min(1, 'Password is required')
    ),
});

const submitRef = useTemplateRef<HTMLButtonElement>('submitRef');

async function onSubmit(values: { email: string; password: string }) {
    isLoading.value = true;
    accountError.value = '';

    try {
        await authStore.login(values.email, values.password);

        if (messageHandoff.value && authStore.user?.role_id !== 2) {
            authStore.logout();
            accountError.value = 'Use a client account to message this expert. Your draft is still saved.';
            return;
        }

        // Check if expert needs data migration
        if (authStore.user && authStore.user.role_id === 3) {
            const res = await expertStore.isUserHasExpert();
            if (res && res.data && res.data.data) {
                if (res.data.data.expert_id === null) {
                    expertStore.migrateUserData();
                }
            }
        }

        const requestedPath = getSafeClientRedirect(authStore.user?.role_id, route.query.redirect)
            || getSafeExpertCheckoutRedirect(authStore.user?.role_id, route.query.redirect);
        if (requestedPath) {
            await router.push(requestedPath);
        } else {
            redirectToDashboard(authStore.user, router);
        }
    } catch (error: any) {
        console.error('[UnifiedLogin] Login error:', error);
        toaster.create({
            title: 'Login failed',
            description: error?.response?.data?.message || 'Invalid email or password.',
            type: 'error',
        });
    } finally {
        isLoading.value = false;
    }
}

function handleLogin() {
    submitRef.value?.click();
}

const signUpClient = () => router.push({ path: '/client/signup', query: handoffQuery.value });
</script>

<template>
    <div class="relative min-h-screen w-screen flex flex-col overflow-hidden">
        <!-- Announcement Bar -->
        <AnnouncementBar class="relative z-20" />

        <div class="relative flex-1 flex items-center justify-center px-4">
            <!-- Background -->
            <div class="absolute inset-0 w-full h-full z-0">
                <AmbientFabricBackground :settings="{ class: 'w-full h-full' }" />
            </div>

            <!-- Login Card -->
        <div class="relative z-10 flex flex-col gap-8 bg-white rounded-md shadow-md p-8 w-full max-w-md">
            <!-- Logo -->
            <UiLogo size="md" />

            <!-- Header -->
            <div class="flex flex-col gap-2">
                <UiText as="h2" variant="h3" class="font-semibold"><span>Login</span></UiText>
                <UiText as="p" variant="p" class="text-se-gray-600"><span>{{ messageHandoff ? "Log in to continue to your conversation. Your message is saved and ready to review." : "Login to your shopexpert account." }}</span></UiText>
            </div>

            <div v-if="accountError" role="alert"><UiText as="p" variant="p-small" :text="accountError" /></div>

            <!-- Login Form -->
            <UiForm v-model="loginModel" :initial-values="initialValues" :schema="loginSchema" @submit="onSubmit">
                <div class="flex flex-col gap-5">
                    <button ref="submitRef" type="submit" class="hidden" />

                    <UiFormField name="email" label="Email" type="email">
                        <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                            <UiInputField
                                :id="id"
                                name="email"
                                type="email"
                                autocomplete="email"
                                :model-value="modelValue as string"
                                :invalid="invalid"
                                :error="error"
                                :disabled="isLoading"
                                placeholder="you@example.com"
                                @update:model-value="onUpdateModelValue"
                                @validate="onValidate"
                            />
                        </template>
                    </UiFormField>

                    <div class="flex flex-col gap-1">
                        <div class="flex items-center justify-between">
                            <UiText as="label" for="password" variant="h6"><span>Password</span></UiText>
                            <RouterLink :to="{ path: '/forgot-password', query: handoffQuery }" class="hover:underline text-se-highlight">
                                <UiText as="span" variant="caption-normal" class="">
                                    <span>Forgot password?</span>
                                </UiText>
                            </RouterLink>
                        </div>
                        <UiFormField name="password" type="password">
                            <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                                <UiInputField
                                    :id="id"
                                    name="password"
                                    type="password"
                                    autocomplete="current-password"
                                    :model-value="modelValue as string"
                                    :invalid="invalid"
                                    :error="error"
                                    :disabled="isLoading"
                                    placeholder="Enter your password"
                                    @update:model-value="onUpdateModelValue"
                                    @validate="onValidate"
                                />
                            </template>
                        </UiFormField>
                    </div>

                    <UiButton
                        type="button"
                        variant="primary"
                        label="Login"
                        class="w-full"
                        :disabled="isLoading"
                        @click="handleLogin"
                    />
                </div>
            </UiForm>

            <!-- Footer: Sign up section -->
            <div class="flex flex-col gap-2">
                <UiText as="p" variant="p-small" class="text-center">
                    <span>Don't have a shopexperts account?</span>
                </UiText>
                <UiButton
                    type="button"
                    variant="outline"
                    label="Create a Client Account"
                    class="w-full"
                    @click="signUpClient"
                />
                <UiText as="p" variant="caption-normal" class="text-center text-se-gray-600">
                    <span>Are you an expert? List your services on shopexperts - </span>
                    <a
                        href="https://dash.shopexperts.com/expert/signup"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="hover:underline text-se-highlight"
                    >
                        <UiText as="span" variant="caption-normal" class="">
                            <span>Apply Now</span>
                        </UiText>
                    </a>
                </UiText>
            </div>
        </div>
        </div>
    </div>
</template>
